import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import VerifyPage from "@/pages/VerifyPage";
import CompleteProfilePage from "@/pages/CompleteProfilePage";
import StaffLoginPage from "@/pages/StaffLoginPage";
import CustomerDashboardPage from "@/pages/customer/CustomerDashboardPage";
import CustomerQrPage from "@/pages/customer/CustomerQrPage";
import CustomerRewardsPage from "@/pages/customer/CustomerRewardsPage";
import CustomerProfilePage from "@/pages/customer/CustomerProfilePage";
import CustomerHistoryPage from "@/pages/customer/CustomerHistoryPage";
import CustomerReferralsPage from "@/pages/customer/CustomerReferralsPage";
import CustomerLayout from "@/components/CustomerLayout";
import StaffLayout from "@/components/StaffLayout";
import AdminLayout from "@/components/AdminLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import ScannerPage from "@/pages/staff/ScannerPage";
import CustomerActionPage from "@/pages/staff/CustomerActionPage";
import StaffProfilePage from "@/pages/staff/StaffProfilePage";
import ActivityLogPage from "@/pages/staff/ActivityLogPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminCustomersPage from "@/pages/admin/AdminCustomersPage";
import AdminCustomerDetailPage from "@/pages/admin/AdminCustomerDetailPage";
import AdminRewardsPage from "@/pages/admin/AdminRewardsPage";
import AdminStaffPage from "@/pages/admin/AdminStaffPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import AdminBrandingPage from "@/pages/admin/AdminBrandingPage";
import PlaceholderPage from "@/components/PlaceholderPage";
import AuthedPlaceholder from "@/components/AuthedPlaceholder";
import { PaperGrainSVG } from "@/components/PaperGrain";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import {
  ProtectedRoute,
  PublicOnlyRoute,
  RequireProfile,
} from "@/components/RouteGuards";

/**
 * Helper for building the "authed + role-checked + profile-required"
 * placeholder routes that customer/staff/admin use until real pages ship.
 */
const Authed = ({ role, title, session, description, testId }) => (
  <ProtectedRoute role={role}>
    <RequireProfile>
      <AuthedPlaceholder
        title={title}
        session={session}
        description={description}
        testId={testId}
      />
    </RequireProfile>
  </ProtectedRoute>
);

function AppRoutes() {
  return (
    <Routes>
      {/* --- Public --- */}
      <Route path="/" element={<LandingPage />} />

      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicOnlyRoute>
            <SignupPage />
          </PublicOnlyRoute>
        }
      />
      {/* /verify must be reachable without an auth check — the verification
          itself is what authenticates the user. */}
      <Route path="/verify" element={<VerifyPage />} />

      {/* Operational PIN-based login for staff + admin (public; logged-in
          users get bounced to their role-home by PublicOnlyRoute).
          /admin/login is an alias — the StaffLoginPage's role-picker handles
          both staff and admin PIN entry. */}
      <Route
        path="/staff/login"
        element={
          <PublicOnlyRoute>
            <StaffLoginPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/admin/login"
        element={<Navigate to="/staff/login" replace />}
      />

      <Route
        path="/complete-profile"
        element={
          <ProtectedRoute role="customer">
            <CompleteProfilePage />
          </ProtectedRoute>
        }
      />

      {/* --- Customer (auth-gated, role>=customer, profile completed) --- */}
      <Route
        element={
          <ProtectedRoute role="customer">
            <RequireProfile>
              <CustomerLayout />
            </RequireProfile>
          </ProtectedRoute>
        }
      >
        <Route path="/customer/dashboard" element={<CustomerDashboardPage />} />
        <Route path="/customer/qr" element={<CustomerQrPage />} />
        <Route path="/customer/rewards" element={<CustomerRewardsPage />} />
        <Route path="/customer/profile" element={<CustomerProfilePage />} />
        <Route path="/customer/history" element={<CustomerHistoryPage />} />
        <Route path="/customer/referrals" element={<CustomerReferralsPage />} />
      </Route>

      {/* --- Staff (>=staff) --- */}
      <Route
        element={
          <ProtectedRoute role="staff">
            <RequireProfile>
              <ErrorBoundary>
                <StaffLayout />
              </ErrorBoundary>
            </RequireProfile>
          </ProtectedRoute>
        }
      >
        <Route path="/staff" element={<Navigate to="/staff/scanner" replace />} />
        <Route path="/staff/scanner" element={<ScannerPage />} />
        <Route path="/staff/customer/:customerId" element={<CustomerActionPage />} />
        <Route path="/staff/profile" element={<StaffProfilePage />} />
        <Route path="/staff/log" element={<ActivityLogPage />} />
      </Route>

      {/* --- Admin (>=admin; Manager deferred to Session 6) --- */}
      <Route
        element={
          <ProtectedRoute role="admin">
            <RequireProfile>
              <ErrorBoundary>
                <AdminLayout />
              </ErrorBoundary>
            </RequireProfile>
          </ProtectedRoute>
        }
      >
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
        <Route path="/admin/customers" element={<AdminCustomersPage />} />
        <Route path="/admin/customer/:customerId" element={<AdminCustomerDetailPage />} />
        <Route path="/admin/rewards" element={<AdminRewardsPage />} />
        <Route path="/admin/staff" element={<AdminStaffPage />} />
        <Route path="/admin/settings" element={<AdminSettingsPage />} />
        <Route path="/admin/branding" element={<AdminBrandingPage />} />
        <Route
          path="/admin/promotions"
          element={
            <AuthedPlaceholder
              title="Promotions"
              session={6}
              description="Multipliers, birthday bonuses, referral bonuses — coming in Session 6."
              testId="page-admin-promotions"
            />
          }
        />
      </Route>

      {/* --- /demo-creds removed in Session 4 PIN-auth pivot (DemoCredsPage
          deleted in Session 6 cleanup). Replaced by /staff/login. --- */}

      {/* --- 404 --- */}
      <Route
        path="*"
        element={
          <PlaceholderPage
            title="Lost in the kitchen"
            session={1}
            description="That page isn't on the menu."
            testId="page-404"
          />
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <PaperGrainSVG />
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-center"
          richColors
          closeButton
          toastOptions={{
            classNames: {
              toast:
                "!bg-napoli-marble !border !border-napoli-gold/40 !text-napoli-espresso !rounded-none !font-body",
              title: "!font-display !text-base",
              description: "!text-napoli-espresso-soft",
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
